import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

type LegalType = 'privacy' | 'terms' | 'disclaimer';

const content: Record<LegalType, { title: string; sections: { heading: string; body: string }[] }> = {
  privacy: {
    title: 'Privacy Policy',
    sections: [
      {
        heading: 'Information We Collect',
        body: 'We do not require you to create an account to browse AllChinaBuy Spreadsheet. When you contact us through our form, we collect the information you voluntarily provide, such as your name and email address, solely to respond to your inquiry.',
      },
      {
        heading: 'How We Use Your Information',
        body: 'Any information you submit is used only to respond to your questions, suggestions, or collaboration requests. We do not sell, rent, or share your personal information with third parties for marketing purposes.',
      },
      {
        heading: 'Cookies & Analytics',
        body: 'This website may use basic cookies and analytics to understand how visitors interact with our content and to improve the overall experience. You can disable cookies in your browser settings at any time.',
      },
      {
        heading: 'Third-Party Links',
        body: 'Our website contains links to external sites, including AllChinaBuy and related shopping platforms. We are not responsible for the privacy practices or content of those third-party websites. We encourage you to review their privacy policies before providing any personal information.',
      },
      {
        heading: 'Data Retention',
        body: 'We retain contact information only as long as necessary to respond to your inquiry and maintain a record of communication. You may request deletion of your information at any time by contacting us.',
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    sections: [
      {
        heading: 'Acceptance of Terms',
        body: 'By accessing or using AllChinaBuy Spreadsheet, you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, please discontinue use of the website.',
      },
      {
        heading: 'Informational Purpose',
        body: 'AllChinaBuy Spreadsheet is an independent informational resource. The content provided, including product links, reviews, and guides, is for general informational purposes only and does not constitute professional advice.',
      },
      {
        heading: 'External Links & Purchases',
        body: 'Product links on this website redirect to external websites. Any purchases you make are subject to the terms and policies of those third-party sites. We are not a party to transactions between you and external sellers or shopping agents.',
      },
      {
        heading: 'Accuracy of Information',
        body: 'While we strive to keep content accurate and up to date, prices, availability, and product details may change without notice. We make no guarantees regarding the completeness or accuracy of any information presented.',
      },
      {
        heading: 'Limitation of Liability',
        body: 'To the fullest extent permitted by law, AllChinaBuy Spreadsheet shall not be liable for any direct, indirect, or consequential damages arising from your use of this website or any external links contained within it.',
      },
    ],
  },
  disclaimer: {
    title: 'Disclaimer',
    sections: [
      {
        heading: 'Independent Resource',
        body: 'This website is an independent informational resource and is not affiliated with, endorsed by, or operated by AllChinaBuy unless explicitly stated. AllChinaBuy is a trademark of its respective owner.',
      },
      {
        heading: 'No Guarantee of Results',
        body: 'We do not guarantee any specific outcomes, including product quality, shipping times, or savings. Shopping internationally carries inherent risks, and you are responsible for your own purchasing decisions.',
      },
      {
        heading: 'Affiliate Disclosure',
        body: 'Some links on this website may be affiliate links. If you make a purchase through such links, we may earn a commission at no additional cost to you. This helps support the continued maintenance of this resource.',
      },
      {
        heading: 'Content Changes',
        body: 'Information on this website may be updated or modified at any time without prior notice. We are not obligated to update any information, including product prices, availability, or promotional offers.',
      },
      {
        heading: 'Not Professional Advice',
        body: 'The content on this website is provided for educational and informational purposes only. It is not intended as legal, financial, or professional advice. Always conduct your own research before making purchasing decisions.',
      },
    ],
  },
};

export default function Legal({ type }: { type: LegalType }) {
  const { t } = useTranslation();
  const data = content[type];

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <div className="pt-20 pb-4 bg-white border-b border-stone-100">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
          <nav className="flex items-center gap-2 text-sm text-stone-500">
            <Link to="/" className="hover:text-stone-900 transition-colors cursor-pointer">{t('breadcrumb_home')}</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-stone-900 font-medium">{data.title}</span>
          </nav>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl border border-stone-200 p-6 md:p-10 shadow-sm">
            <h1 className="text-2xl md:text-3xl font-bold text-stone-900 mb-2">{data.title}</h1>
            <p className="text-sm text-stone-500 mb-8">
              {t('article_last_updated')}: August 25, 2026
            </p>

            <div className="space-y-8">
              {data.sections.map((section) => (
                <div key={section.heading}>
                  <h2 className="text-lg font-bold text-stone-900 mb-3">{section.heading}</h2>
                  <p className="text-stone-600 leading-relaxed">{section.body}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 pt-6 border-t border-stone-200">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                {t('article_back_home')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}